import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { DashboardError, DashboardPage } from '../../components/JobsAccount';
import { book } from '../../routers/books';
import {
  getResumeDatabaseErrorMessage,
  useGetResumeDatabaseAccessQuery,
  useGetResumeIndustrySectorsQuery,
  useSearchResumesQuery,
} from '../../services/serviceResume';
import { getErrorStatus, isForbidden } from '../../utils/jobAccessError';
import {
  ListingAllowanceSummary,
  ResumeDatabaseHistoricalOnly,
  ResumeDatabaseSuspended,
  ResumeDatabaseTerms,
} from './accessPanels';
import { EmployerRequestHistory } from './requestHistory';
import { ResumeDatabaseUpsell } from './upsell';

interface IResumeFilters {
  keyword: string;
  location: string;
  sectorId: number | '';
  willing: boolean;
}

const emptyFilters: IResumeFilters = {
  keyword: '',
  location: '',
  sectorId: '',
  willing: false,
};

const DEFAULT_PAGE_SIZE = 20;

export const ResumeDatabase = () => {
  // `draft` is what the employer is typing; `applied` is what the server was
  // asked for. Searching is an explicit action, never a keystroke.
  const [draft, setDraft] = useState<IResumeFilters>(emptyFilters);
  const [applied, setApplied] = useState<IResumeFilters>(emptyFilters);
  const [page, setPage] = useState(1);
  const [showRequests, setShowRequests] = useState(false);

  const access = useGetResumeDatabaseAccessQuery();
  const state = access.data?.state;
  const pageSize = Math.min(DEFAULT_PAGE_SIZE, access.data?.maxSearchPageSize || DEFAULT_PAGE_SIZE);
  const sectors = useGetResumeIndustrySectorsQuery(undefined, { skip: state !== 'granted' });
  const search = useSearchResumesQuery({
    keyword: applied.keyword || undefined,
    location: applied.location || undefined,
    industrySectorIds: applied.sectorId ? [applied.sectorId] : undefined,
    willingToRelocate: applied.willing || undefined,
    page,
    pageSize,
  }, { skip: showRequests || state !== 'granted' });

  // A failed access call is the only place a legacy 403 still decides the page.
  const accessForbidden = isForbidden(access.error);
  const showUpsell = !showRequests && (state === 'notEligible' || accessForbidden);
  const searchMessage = search.error
    ? getResumeDatabaseErrorMessage(search.error)
    : null;
  const isRateLimited = getErrorStatus(search.error) === 429;

  const applyFilters = () => {
    setApplied(draft);
    setPage(1);
  };

  const clearFilters = () => {
    setDraft(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
  };

  return (
    <DashboardPage
      title="Resume Database"
      isLoading={access.isLoading || (!showRequests && state === 'granted' && search.isLoading)}
      noAccessComponent={showUpsell ? (
        <ResumeDatabaseUpsell onShowRequests={() => setShowRequests(true)} />
      ) : undefined}
      actions={(
        <Button variant="outlined" onClick={() => setShowRequests((value) => !value)}>
          {showRequests ? 'Search resumes' : 'My requests'}
        </Button>
      )}
    >
      {!showUpsell && !accessForbidden ? (
        <DashboardError
          error={access.error}
          fallback="We could not check your Resume Database access."
        />
      ) : null}

      {showRequests ? <EmployerRequestHistory /> : null}

      {!showRequests && state === 'termsRequired' && access.data ? (
        <ResumeDatabaseTerms access={access.data} />
      ) : null}

      {!showRequests && state === 'suspended' && access.data ? (
        <ResumeDatabaseSuspended access={access.data} />
      ) : null}

      {!showRequests && state === 'historicalOnly' ? (
        <ResumeDatabaseHistoricalOnly onShowRequests={() => setShowRequests(true)} />
      ) : null}

      {!showRequests && state === 'granted' ? (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Candidate privacy:</strong> email, phone, address, and resume files are never
            shown in search. Request an introduction for one of your Featured listings, and the
            candidate decides whether to share them. A Public profile shows the candidate&rsquo;s
            name, not their contact details.
          </Alert>
          {searchMessage ? (
            <Alert severity={isRateLimited ? 'warning' : 'error'} sx={{ mb: 3 }}>
              {searchMessage}
            </Alert>
          ) : null}
          {!searchMessage ? (
            <DashboardError error={search.error} fallback="We could not search the resume database." />
          ) : null}
          <ListingAllowanceSummary listings={access.data?.listings || []} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ borderRadius: 0, p: 2, position: { md: 'sticky' }, top: { md: 24 } }}>
                <Stack
                  component="form"
                  spacing={2}
                  onSubmit={(event: React.FormEvent) => {
                    event.preventDefault();
                    applyFilters();
                  }}
                >
                  <Typography component="h2" variant="h6">Filters</Typography>
                  <TextField
                    value={draft.keyword}
                    onChange={(event) => setDraft({ ...draft, keyword: event.target.value })}
                    label="Keyword"
                    helperText="Matches skills, sectors, headline and overview, not candidate names."
                    InputProps={{ endAdornment: <SearchIcon /> }}
                  />
                  <TextField
                    value={draft.location}
                    onChange={(event) => setDraft({ ...draft, location: event.target.value })}
                    label="Location"
                  />
                  <TextField
                    select
                    value={draft.sectorId}
                    onChange={(event) => setDraft({
                      ...draft,
                      sectorId: event.target.value ? Number(event.target.value) : '',
                    })}
                    label="Industry sector"
                  >
                    <MenuItem value="">All sectors</MenuItem>
                    {(sectors.data || []).map((sector) => (
                      <MenuItem key={sector.id} value={sector.id}>{sector.name}</MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={draft.willing}
                        onChange={(_event, checked) => setDraft({ ...draft, willing: checked })}
                      />
                    )}
                    label="Willing to relocate"
                  />
                  <Button type="submit" variant="contained" disabled={search.isFetching}>
                    Apply filters
                  </Button>
                  <Button type="button" variant="text" onClick={clearFilters}>Clear filters</Button>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography sx={{ fontWeight: 700, mb: 2 }}>
                {search.data?.total || 0} candidates
              </Typography>
              <Stack spacing={2}>
                {(search.data?.results || []).map((candidate) => (
                  <Paper key={candidate.rguid} variant="outlined" sx={{ borderRadius: 0, p: 2.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography component="h2" variant="h6">{candidate.displayName}</Typography>
                          {candidate.identityHidden ? (
                            <Chip icon={<VisibilityOffOutlinedIcon />} label="Name hidden" size="small" />
                          ) : (
                            <Chip label="Name shared" size="small" variant="outlined" />
                          )}
                        </Stack>
                        <Typography fontWeight={800}>{candidate.headline || 'CPG professional'}</Typography>
                        <Typography color="text.secondary">{candidate.primaryLocation || 'Location not listed'}</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          {candidate.industrySectors.map((sector) => <Chip key={sector} label={sector} size="small" variant="outlined" />)}
                        </Stack>
                      </Box>
                      {/* The row Stack is flex, so without alignSelf this button stretches to the
                          full height of the detail column and shrinks until the label wraps. */}
                      <Button
                        component={Link}
                        to={book.resumeDatabaseDetail(candidate.rguid)}
                        variant="outlined"
                        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        View resume
                      </Button>
                    </Stack>
                    <Typography color="text.secondary" variant="caption" sx={{ display: 'block', mt: 2 }}>
                      Updated {candidate.lastUpdated ? moment(candidate.lastUpdated).format('MMM D, YYYY') : 'date unavailable'}
                    </Typography>
                  </Paper>
                ))}
                {search.data && !search.data.results.length ? (
                  <Alert severity="info">No searchable resumes match these filters.</Alert>
                ) : null}
              </Stack>
              {search.data && search.data.total > pageSize ? (
                <Pagination
                  count={Math.ceil(search.data.total / pageSize)}
                  page={page}
                  onChange={(_event, value) => setPage(value)}
                  sx={{ mt: 3 }}
                />
              ) : null}
            </Grid>
          </Grid>
        </>
      ) : null}
    </DashboardPage>
  );
};

export default ResumeDatabase;
